import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardHeader, CardBody, CardTitle, Table } from "reactstrap";
import { formatDateForDisplay } from "../../../utils/dateUtils";
import { formatPriceWithDollar } from "../../../utils/currency";
import { resolveInsuranceDisplayStatus } from "../../../utils/insuranceStatus";

const normalizeInsuranceHistory = (data) => {
  const insuranceHistory = Array.isArray(data) ? data : [];

  return insuranceHistory.map((item, idx) => {
    const status = resolveInsuranceDisplayStatus(item, {
      // History rows keep Renewed/New labels; only API "expired" overrides.
      // Current policy on the card/list uses expire-date override.
      applyExpireOverride: item.action === "update" || item.action === "new" || !item.action,
    });
    return {
      id: item.id || idx,
      insurance_policy_number: item.insurance_policy_number || '-',
      action: item.action || '-',
      status,
      insurance_agent: `${item.insurance_agent_name || '-'}\n${item.insurance_agent_phone || '-'}`,
      insurance_company: `${item.insurance_company_name || '-'}\n${item.insurance_company_phone || '-'}`,
      dates: `${formatDateForDisplay(item.effective_date)}\n${formatDateForDisplay(item.expire_date)}`,
      deductibles: `${formatPriceWithDollar(item.collision_deductible)}\n${formatPriceWithDollar(item.comprehensive_deductible)}`,
    };
  });
};

const AccountsInsuranceTab = ({ data, activeColor = "secondary" }) => {
  const { t } = useTranslation();
  const rows = useMemo(() => normalizeInsuranceHistory(data), [data]);

  return (
    <div className="container-fluid px-0">
      <Card>
        <CardBody className="pt-2" style={{ maxHeight: 600, overflowY: "auto" }}>
          {rows.length === 0 ? (
            <div className="text-center text-muted py-5">{t('accountsInsuranceTab.noInsuranceHistory')}</div>
          ) : (
            <Table responsive style={{fontSize: 13}}>
              <thead className="text-primary">
                <tr>
                  <th>{t('accountsInsuranceTab.policyNumber')}</th>
                  <th>{t('accountsInsuranceTab.action')}</th>
                  <th>{t('accountsInsuranceTab.insuranceAgent')}</th>
                  <th>{t('accountsInsuranceTab.insuranceCompany')}</th>
                  <th>{t('accountsInsuranceTab.effectiveDate')}
                    <br />
                    {t('accountsInsuranceTab.expireDate')}
                  </th>
                  <th>{t('accountsInsuranceTab.compDeduct')}
                    <br />
                    {t('accountsInsuranceTab.collDeduct')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.id}>
                    <td>{row.insurance_policy_number}</td>
                    <td>
                      <span
                        style={{
                          textTransform: "capitalize",
                          color: row.status.color,
                          fontWeight:
                            row.status.kind === "expired" ||
                            row.status.kind === "cancelled" ||
                            row.status.kind === "none"
                              ? "bold"
                              : "normal",
                        }}
                      >
                        {row.status.translationKey
                          ? t(
                              `accountsInsuranceTab.${row.status.translationKey}`,
                              row.status.fallbackLabel
                            )
                          : row.status.fallbackLabel || row.action}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'pre-line' }}>{row.insurance_agent}</td>
                    <td style={{ whiteSpace: 'pre-line' }}>{row.insurance_company}</td>
                    <td style={{ whiteSpace: 'pre-line' }}>{row.dates}</td>
                    <td style={{ whiteSpace: 'pre-line' }}>{row.deductibles}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default AccountsInsuranceTab;
